(ns app.front.view.workspace.search-mode
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.dashboard.search :as search]
            [app.front.search-result :as sr]
            [app.front.ui :as ui]
            [app.front.ui.messages-panel :as messages-panel]
            [app.front.ui.panels :as panels]
            [app.front.ui.updated :as ui-updated]
            [app.front.util :refer [for-frag]]
            [app.front.view.conversations.core :as conversations]
            [app.front.view.search.list-view :as list-view]
            [app.front.view.workspace.header :as ws-header]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(defn- state->name [tr phase-state]
  (let [state-ns (namespace phase-state)]
    (str (tr [phase-state])
         (when (not= state-ns "state.application")
           (str
             " ("
             (->> (str/split state-ns #"\.")
                  first
                  (keyword "workspace.state")
                  vector
                  tr)
             ")")))))

(defn- format-category [tr all-categories [permit-type category]]
  (let [permit-type-count (->> all-categories
                               (map first)
                               distinct
                               count)]
    (if (= 1 permit-type-count)
      category
      (str category " (" (tr [permit-type]) ")"))))


(defn- val->str-fn [search-config tr attribute]
  (case attribute
    :workspace/category            (partial format-category tr (-> search-config :filter-values :workspace/category))
    :workspace/permit-type         #(tr [%])
    :completion-date               #(tr [%])
    :application/work-target-names #(tr [%])
    :application/work-type-names   #(tr [%])
    :domain/state                  (partial state->name tr)
    :group/id                      (fn [v]
                                     (-> search-config
                                         :filter-values
                                         :group/id
                                         (p/find-first #(= v (first %)))
                                         second))
    :municipality/number           (fn [v]
                                     (-> search-config
                                         :filter-values
                                         :municipality/number
                                         (p/find-first #(= v (first %)))
                                         second))
    (partial search/db-id->name search-config attribute tr)))

(defn- filter-value [attribute value]
  (case attribute
    (:group/id :municipality/number) (first value)
    (:workspace/assignee :review/assignee :approval/assignee :approval/user-group) (or (:db/id value) value)
    value))

(defn- cluster-results [results]
  (->> results
       (group-by :workspace/address)
       (p/map-vals (fn [vals]
                     (->> (group-by #(select-keys % [:project/name :project-id]) vals)
                          (p/map-vals (partial group-by
                                               #(select-keys % [:phase-state
                                                                :workspace-state
                                                                :workspace/permit-type]))))))))

(defn- render-clustered-results [address+results open-result-view tr lang]
  (for-frag [[address project+results] address+results]
    [:div.mb-3
     [:h4.uppercase.text-sm
      address]
     (for-frag [[{:project/keys [name] :as project} state+results] project+results]
       [:div.mt-2
        [:div
         (cc/link
           {:href       (href/href :view/project (select-keys project [:project-id]))
            :class-name "text-xs"}
           name)]
        (for-frag [[state results] state+results]
          [:div.mt-2
           [:div.text-xs.font-bold (ws-util/workspace-state tr state)]
           (for-frag [{:workspace/keys [permit-type category-names municipal-case-id] :as result} results
                      :let [permit-type-label (ws-util/permit-type-label tr lang result)]]
             [:div.w-full
              [:button.p-3.bg-white.mt-2.w-full.border-0.text-left.select-search-result-button.flex.items-center
               {:on-click #(open-result-view result)}
               (if-let [icon (ui/permit-type-icon permit-type)]
                 [:span.mr-2 {:style {:height     "16px"
                                      :margin-top "-8px"}
                              :class (str "text-" (ui/permit-type-color permit-type))
                              :title permit-type-label}
                  (r/create-element icon #js{:aria-label permit-type-label})]
                 [:span {:style {:width "32px"}}])
               [:span.font-bold
                (i18n-model/get-text-from-entities-or-map category-names lang)]
               (when municipal-case-id
                 [:span.ml-2 municipal-case-id])]])])])]))

(defn- applicants-cell [{:keys [applicants]}]
  (str/join ", " applicants))

(defn- list-view-cols [{:keys [tr lang val->str-fn sort-options]}]
  [[:search-filter-attribute/main-permit-type
    (partial ws-util/permit-type-label tr lang)
    {}]
   [:search/project :project/name {:truncate? true}]
   [:workspace/address (partial ws-util/search-result-address tr lang) {}]
   [:search-filter-attribute/domain-state (partial ws-util/workspace-state tr) {}]
   [:search/applicants applicants-cell {:truncate? true}]
   [:search/department (comp (val->str-fn :group/id) :group/id) {}]
   [:updated
    (fn [{:keys [modified modified-by]}]
      [ui-updated/updated {:ts modified :user modified-by}])
    (list-view/sortable-col sort-options :modified)]])

(defn- list-view-link [{:keys [tr]}]
  {:make-href   #(ws-util/workspace-or-resume-application-href
                   % (ws-util/workspace-href %) (sr/search-origin-query))
   :description #(tr [(ws-util/workspace-link-description %)])})

(rf/reg-event-fx ::toggle-messages
  (fn [{:keys [db]} [_ workspace-id]]
    (if (and (= :messages (-> db :ui/open-panel first))
             (not= workspace-id (get-in db [::conversations/state ::conversations/selected-workspace-id])))
      {:fx [[:dispatch [::panels/open :messages]]
            [:dispatch [::messages-panel/init workspace-id]]]}
      {:fx [[:dispatch [::messages-panel/toggle-open workspace-id]]]})))

(defn- list-view-actions [{:keys [tr]} {:workspace/keys [id] :keys [notifications-disabled?] :as workspace}]
  [(when-not (<sub [:session/authority-user?])
     {:title    (tr [(if notifications-disabled? :search/enable-email :search/disable-email)])
      :icon     (if notifications-disabled? i/envelope-off i/envelope)
      :on-click #(do
                   (>evt [::ws-header/email-subscription workspace notifications-disabled?])
                   (>evt [::search/toggle-workspace-notifications id notifications-disabled?]))})
   {:title    (tr [:navigation/messages])
    :icon     i/conversation
    :on-click #(>evt [::toggle-messages id])}])

(def search-mode {:name                        :workspace
                  :title                       :dashboard-search/title
                  :subtitle                    :dashboard-search/instructions
                  :search-config-command       :user/search-config
                  :search-command              :workspace/find
                  :save-filter-command         :user/upsert-filter
                  :remove-filter-command       :user/remove-filter
                  :id-fn                       :workspace/id
                  :filter-subtype              :filter.subtype/workspace
                  :filter-value                filter-value
                  :button-filters              #{:workspace/permit-type}
                  :prefers-list-view-flag      :user.config-flag/prefers-dashboard-list-view?
                  :filter-condition-attributes workspace-model/dashboard-filter-condition-attributes
                  :val->str-fn                 val->str-fn
                  :render-search-result        (fn [result options]
                                                 ^{:key (str "ws-result-" (:workspace/id result))}
                                                 [sr/search-result-box result options
                                                  [sr/dashboard-result-footer result]])
                  :cluster-fn                  cluster-results
                  :render-clustered-results    render-clustered-results
                  :map-search-command          :workspace/find-for-map
                  :list-view-cols              list-view-cols
                  :list-view-link              list-view-link
                  :list-view-actions           list-view-actions
                  :default-view                :dashboard-search/map-view
                  :feature-gated-filter-attributes
                  {:feature-flag.id/lower-tier-municipality-workspace-filter #{:municipality/number}}})
